import { DayBookings, BookingItem} from './types/bookings';
import { ServiceType } from './types/services';
import { Contact,ContactSubscription } from './types/contacts';
import { ContactHistoryStats } from './types/analytics';
import { getDefaultAvatar } from './utils/avatarUtils';
import { TaskItem } from './types/tasks';

// Lists of realistic first and last names for test data
const firstNames = [
  "James", "Mary", "John", "Patricia", "Robert", "Jennifer", "Michael", "Linda",
  "William", "Elizabeth", "David", "Barbara", "Richard", "Susan", "Joseph", "Jessica",
  "Thomas", "Sarah", "Charles", "Karen", "Christopher", "Nancy", "Daniel", "Lisa",
  "Matthew", "Betty", "Anthony", "Margaret", "Mark", "Sandra", "Donald", "Ashley",
  "Steven", "Kimberly", "Paul", "Emily", "Andrew", "Donna", "Joshua", "Michelle",
  "Sofia", "Olivia", "Emma", "Ava", "Isabella", "Mia", "Zoe", "Lily", "Emily",
  "Madison", "Amelia", "Sophia", "Charlotte", "Harper", "Evelyn", "Abigail"
];

const lastNames = [
  "Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis",
  "Rodriguez", "Martinez", "Hernandez", "Lopez", "Gonzalez", "Wilson", "Anderson",
  "Thomas", "Taylor", "Moore", "Jackson", "Martin", "Lee", "Perez", "Thompson",
  "White", "Harris", "Sanchez", "Clark", "Ramirez", "Lewis", "Robinson", "Walker",
  "Young", "Allen", "King", "Wright", "Scott", "Torres", "Nguyen", "Hill", "Flores",
  "Chen", "Singh", "Patel", "Kim", "Choi", "Park", "Wu", "Liu", "Gupta", "Sharma"
];

const services: ServiceType[] = [
  'Deep Cleaning',
 'Move Out Cleaning',
   'Window Cleaning',
   'Free Estimate',
   'Refrigerator Cleaning',
   'Post Construction Cleaning',
   'Recurring Cleaning',
   'Oven Cleaning',
   'AirBnb Cleaning Service',
   'Commercial Cleaning', 
   'Office Cleaning'

];

export const staffMembers = [
  { id: 'staff1', name: 'John Doe', avatar: getDefaultAvatar('John Doe') },
  { id: 'staff2', name: 'Jane Smith', avatar: getDefaultAvatar('Jane Smith') },
  { id: 'staff3', name: 'Mike Johnson', avatar: getDefaultAvatar('Mike Johnson') },
  { id: 'staff4', name: 'Sarah Williams', avatar: getDefaultAvatar('Sarah Williams') }
];

const staffMemberNames = ["Maria G.", "Robert K.", "Sarah L.", "John Smith", "Lisa M."];
const resources = ["Team A", "Team B", "Team C", "Team D"];

// Helper to get a random item from an array
const getRandomItem = <T>(array: T[]): T => {
  return array[Math.floor(Math.random() * array.length)];
};

// -------------------- CONTACTS MANAGEMENT --------------------

// Add this export to make the contacts accessible to other files
export let staticContacts: Contact[] = [];

// Update initializeContacts to be exported and return the static contacts
export const initializeContacts = (count: number = 20): Contact[] => {
  if (staticContacts.length === 0) {
    staticContacts = Array(count).fill(null).map((_, index) => {
      const firstName = getRandomItem(firstNames);
      const lastName = getRandomItem(lastNames);
      const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`;

      return {
        id: `contact-${index}`,
        _id: `contact-${index}`,
        firstName,
        lastName,
        email,
        emailType: 'primary',
        phone: `555-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`,
        phoneType: 'mobile',
        phones: [],
        emails: [{
          id: `email-${index}`,
          email: email,
          type: 'personal'
        }],
        addresses: [],
        addressLine1: `${100 + Math.floor(Math.random() * 900)} Main Street`,
        addressLine2: Math.random() > 0.5 ? `Apt ${Math.floor(Math.random() * 100)}` : '',
        city: 'Sample City',
        state: 'ST',
        country: 'USA',
        zipCode: String(10000 + Math.floor(Math.random() * 90000)),
        addressType: 'home',
        address: `${100 + Math.floor(Math.random() * 900)} Main Street`, // Add address property
        avatar: getDefaultAvatar(`${firstName} ${lastName}`),
        customFields: [],
        notes: [],
        createdAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date().toISOString()
      };
    });
  }
  return staticContacts;
};

// Export function to get contacts
export const generateContacts = (count: number = 20): Contact[] => {
  return initializeContacts(count);
};

// -------------------- BookingS MANAGEMENT --------------------

// Cache for bookings to prevent regeneration
const bookingCache = new Map<string, DayBookings[]>();

// Generate bookings using the same contacts
export const getStaticBookings = (selectedDate: Date): DayBookings[] => {
  // Create a cache key based on the selected date
  const cacheKey = selectedDate.toISOString().split('T')[0];

  // Check if we already have data for this date
  if (bookingCache.has(cacheKey)) {
    return bookingCache.get(cacheKey)!;
  }

  // Make sure contacts are initialized
  const contacts = initializeContacts();

  // Generate dates for the week
  const baseDate = new Date(selectedDate);
  baseDate.setHours(0, 0, 0, 0);

  const dates = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(baseDate);
    date.setDate(date.getDate() + i);
    dates.push(date);
  }

  // Create bookings with proper date objects
  const result: DayBookings[] = dates.map(date => {
    // Limit the number of bookings to reduce processing
    const bookingCount = Math.min(2, Math.floor(Math.random() * 3) + 1);

    const dateString = date.toISOString().split('T')[0];

    return {
      date: date,
      items: Array.from({ length: bookingCount }, (_, i) => {
        const hour = Math.floor(Math.random() * 8) + 9;
        const startTimeString = `${hour.toString().padStart(2, '0')}:00`;
        const endHour = hour + 1;
        const endTimeString = `${endHour.toString().padStart(2, '0')}:00`;

        // Use a contact from our static contacts array
        const contactIndex = Math.floor(Math.random() * contacts.length);
        const contact = contacts[contactIndex];

        return {
          id: `booking-${dateString}-${i}`,
          title: `booking ${i + 1}`,
          startTime: startTimeString,
          endTime: endTimeString,
          serviceType: getRandomItem(services),
          staff: getRandomItem(staffMemberNames),
          resource: getRandomItem(resources),
          status: Math.random() > 0.5 ? "confirmed" : "pending" as "confirmed" | "pending" | "completed" | "cancelled",
          bookingId: `booking-${dateString}-${i}`,
          date: dateString,
          contactId: contact.id,
          contact: {
            id: contact.id,
            name: `${contact.firstName} ${contact.lastName}`,
            email: contact.emails[0]?.email,
            phone: contact.phones[0]?.phone || '',
            address: contact.addressLine1 || '',
            avatarUrl: contact.avatar
          }
        };
      })
    };
  });

  // Store in cache for future use
  bookingCache.set(cacheKey, result);

  return result;
};
// Helper function to find an booking by ID
export const findBooking = (id: string): BookingItem | undefined => {
  // Look through all bookings from all days to find the one with matching ID
  for (const day of getStaticBookings(new Date())) {
    const found: BookingItem | undefined = day.items.find((booking: BookingItem) => booking.id === id);
    if (found) {
      return found;
    }
  }
  return undefined;
};

// Legacy function for backward compatibility
export const generateTestData = (daysToGenerate: number): DayBookings[] => {
  const currentDate = new Date();
  // Use our static contacts
  const contacts = initializeContacts();

  const testData = Array(daysToGenerate).fill(null).map((_, dayIndex) => {
    const date = new Date(currentDate);
    date.setDate(currentDate.getDate() + dayIndex);

    const bookingsCount = Math.floor(Math.random() * 3) + 1;
    return {
      date,
      items: Array(bookingsCount).fill(null).map((_, itemIndex) => {
        // Use a contact from our static contacts array
        const contactIndex = Math.floor(Math.random() * contacts.length);
        const contact = contacts[contactIndex];

        return {
          id: `test-${dayIndex}-${itemIndex}`,
          startTime: `${9 + Math.floor(Math.random() * 8)}:00`,
          endTime: `${14 + Math.floor(Math.random() * 4)}:00`,
          serviceType: getRandomItem(services),
          staff: getRandomItem(staffMemberNames),
          resource: getRandomItem(resources),
          status: Math.random() > 0.5 ? "confirmed" : "pending" as "confirmed" | "pending" | "completed" | "cancelled",
          bookingId: `booking-${dayIndex}-${itemIndex}`,
          date: date.toISOString().split('T')[0],
          title: `booking ${itemIndex + 1}`,
          contactId: contact.id,
          contact: {
            id: contact.id,
            name: `${contact.firstName} ${contact.lastName}`,
            email: contact.emails[0]?.email,
            phone: contact.phones[0]?.phone || '',
            address: contact.address || '',
            avatarUrl: contact.avatar
          }
        };
      })    };
  });

  return testData;
};

// -------------------- SUBSCRIPTIONS MANAGEMENT --------------------

// Define frequencies with exact types that match ContactSubscription
const frequencies = ['weekly', 'bi-weekly', 'monthly', 'one-time'] as const;
type ValidFrequency = typeof frequencies[number];

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const planNames = [
  'Weekly Cleaning', 'Bi-weekly Deep Clean', 'Monthly Maintenance',
  'Quarterly Deep Clean', 'Premium Service Plan', 'AirBnb Weekly Refresh'
];

// Cache subscriptions to prevent regeneration
const subscriptionCache = new Map<string, ContactSubscription[]>();

export const getContactSubscriptions = (contactId: string): ContactSubscription[] => {
  console.log('Generating subscriptions for contact:', contactId);

  if (subscriptionCache.has(contactId)) {
    return subscriptionCache.get(contactId)!;
  }

  const hasSubscription = true;

  if (!hasSubscription) {
    subscriptionCache.set(contactId, []);
    return [];
  }

  const subscriptionCount = Math.floor(Math.random() * 2) + 1;
  console.log(`Generating ${subscriptionCount} subscriptions for contact ${contactId}`);

  const subscriptions: ContactSubscription[] = Array(subscriptionCount).fill(null).map((_, index) => {
    const freqIndex = Math.floor(Math.random() * frequencies.length);
    // Cast to the expected type
    const frequency = frequencies[freqIndex] as ValidFrequency;
    
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - Math.floor(Math.random() * 180));

    const nextBillingDate = new Date();
    nextBillingDate.setDate(nextBillingDate.getDate() + Math.floor(Math.random() * 30) + 1);

    return {
      id: `sub-${contactId}-${index}`,
      name: planNames[Math.floor(Math.random() * planNames.length)],
      type: frequency,
      contactId,
      planName: planNames[Math.floor(Math.random() * planNames.length)],
      status: Math.random() < 0.9 ? 'active' : 
              (Math.random() < 0.5 ? 'paused' : 
              (Math.random() < 0.5 ? 'inactive' : 'expired')),
      nextDate: nextBillingDate.toISOString().split('T')[0],
      nextBillingDate: nextBillingDate.toISOString(),
      amount: Math.round((50 + Math.random() * 250) * 100) / 100,
      frequency,
      dayOfWeek: frequency === 'weekly' || frequency === 'bi-weekly' ?
        daysOfWeek[Math.floor(Math.random() * daysOfWeek.length)] : undefined,
      dayOfMonth: frequency === 'monthly' || frequency === 'one-time' ?
        `${Math.floor(Math.random() * 28) + 1}${['st', 'nd', 'rd', 'th'][Math.min(3, Math.floor(Math.random() * 28))]}` : undefined,
      startDate: startDate,
    };
  });

  subscriptionCache.set(contactId, subscriptions);
  console.log('Generated subscriptions:', subscriptions);

  return subscriptions;
};

// Add a function to get all subscriptions (for admin views)
export const getAllSubscriptions = (): ContactSubscription[] => {
  const allSubscriptions: ContactSubscription[] = [];
  const contacts = initializeContacts();

  for (const contact of contacts) {
    const contactSubscriptions = getContactSubscriptions(contact.id);
    allSubscriptions.push(...contactSubscriptions);
  }

  return allSubscriptions;
};

// Client history stats
export const getContactHistoryStats = (contactId: string, period: string): ContactHistoryStats => {
  // Generate consistent stats based on contactId and period
  const contactSeed = contactId.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
  const baseServices = (contactSeed % 20) + 1; // 1-20 services
  const baseSpent = ((contactSeed % 30) + 1) * 100; // $100-$3000

  // Adjust based on period
  let periodMultiplier = 1;

  switch (period) {
    case '30':
      periodMultiplier = 0.3;
      break;
    case '60':
      periodMultiplier = 0.6;
      break;
    case '90':
      periodMultiplier = 0.9;
      break;
    case 'all':
    default:
      periodMultiplier = 1;
      break;
  }

  // Calculate service count and spend amount
  const serviceCount = Math.floor(baseServices * periodMultiplier);
  const totalSpent = Math.round((baseSpent * periodMultiplier) * 100) / 100;

  return {
    totalServices: serviceCount,
    totalSpent: totalSpent,
    averageServiceValue: serviceCount > 0 ? Math.round((totalSpent / serviceCount) * 100) / 100 : 0,
    totalBookings: serviceCount + Math.floor(Math.random() * 5),
    cancelledBookings: Math.floor(Math.random() * 3),
    completedBookings: serviceCount
  };
};

// -------------------- TASKS MANAGEMENT --------------------

// Mock tasks cache
const taskCache = new Map<string, TaskItem[]>();

// Generate tasks for a specific contact
export const getContactTasks = (contactId: string): TaskItem[] => {
  if (taskCache.has(contactId)) {
    return taskCache.get(contactId)!;
  }

  const taskCount = Math.floor(Math.random() * 5); // 0-4 tasks
  const tasks: TaskItem[] = [];

  for (let i = 0; i < taskCount; i++) {
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + Math.floor(Math.random() * 14)); // Due in 0-14 days
    
    const priority = ['low', 'medium', 'high'][Math.floor(Math.random() * 3)] as 'low' | 'medium' | 'high';
    const status = Math.random() > 0.3 ? 'pending' : 'completed';
    
    const task: TaskItem = {
      id: `task-${contactId}-${i}-${Date.now()}`,
      title: [
        'Follow up on quote',
        'Schedule consultation',
        'Send contract',
        'Review cleaning requirements',
        'Call about special cleaning needs',
        'Verify booking details',
        'Update client preferences',
        'Confirm billing information'
      ][Math.floor(Math.random() * 8)],
      dueDate: dueDate.toISOString(),
      priority,
      status,
      completedAt: status === 'completed' ? new Date().toISOString() : null,
      createdAt: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
      notes: Math.random() > 0.5 ? 'Some notes about this task' : '',
      assignedToId: staffMembers[Math.floor(Math.random() * staffMembers.length)].id,
      contactId
    };
    
    tasks.push(task);
  }
  
  taskCache.set(contactId, tasks);
  return tasks;
};

// Add a new task
export const addContactTask = (contactId: string, taskData: Partial<TaskItem>): TaskItem => {
  const newTask: TaskItem = {
    id: `task-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    title: taskData.title || 'Untitled Task',
    dueDate: taskData.dueDate || null,
    priority: taskData.priority || 'medium',
    status: 'pending',
    completedAt: null,
    createdAt: new Date().toISOString(),
    notes: taskData.notes || '',
    assignedToId: taskData.assignedToId || '',
    contactId
  };
  
  const existingTasks = taskCache.get(contactId) || [];
  const updatedTasks = [...existingTasks, newTask];
  taskCache.set(contactId, updatedTasks);
  
  return newTask;
};

// Complete a task
export const completeContactTask = (contactId: string, taskId: string): boolean => {
  const tasks = taskCache.get(contactId) || [];
  const taskIndex = tasks.findIndex(t => t.id === taskId);
  
  if (taskIndex > -1) {
    const updatedTasks = [...tasks];
    updatedTasks[taskIndex] = {
      ...updatedTasks[taskIndex],
      status: 'completed',
      completedAt: new Date().toISOString()
    };
    
    taskCache.set(contactId, updatedTasks);
    return true;
  }
  
  return false;
};

// Update a task
export const updateContactTask = (contactId: string, taskId: string, updates: Partial<TaskItem>): TaskItem | null => {
  const tasks = taskCache.get(contactId) || [];
  const taskIndex = tasks.findIndex(t => t.id === taskId);
  
  if (taskIndex > -1) {
    const updatedTasks = [...tasks];
    updatedTasks[taskIndex] = {
      ...updatedTasks[taskIndex],
      ...updates,
      completedAt: new Date().toISOString()
    };
    
    taskCache.set(contactId, updatedTasks);
    return updatedTasks[taskIndex];
  }
  
  return null;
};

// Delete a task
export const deleteContactTask = (contactId: string, taskId: string): boolean => {
  const tasks = taskCache.get(contactId) || [];
  const taskIndex = tasks.findIndex(t => t.id === taskId);
  
  if (taskIndex > -1) {
    const updatedTasks = [...tasks];
    updatedTasks.splice(taskIndex, 1);
    
    taskCache.set(contactId, updatedTasks);
    return true;
  }
  
  return false;
};